import { yupResolver } from '@hookform/resolvers/yup';
import { isSupported } from 'firebase/messaging';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import OTPInput from 'components/field/OTPInput';
import PasswordInput from 'components/field/PasswordInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useChangeLanguage from 'hooks/useChangeLanguage';
import useLocale from 'hooks/useLocale';
import useRefreshToken from 'hooks/useRefreshToken';

import { axiosAuth } from 'services/axios';
import { AUTH } from 'services/consts';
import { deleteFCMToken } from 'services/firebase';

import AppLogger from 'utils/AppLogger';
import { PLATFORM } from 'utils/constants';
import { languages } from 'utils/localization';
import PATHS from 'utils/paths';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Common.styled';
import SigninWithGoogle from './SigninWithGoogle';
import useFedCM from './useFedCM';

const titles = {
	/* eslint-disable camelcase */
	en_US: 'Login - OTO',
	ar_SA: 'تسجيل الدخول - أوتو',
	tr_TR: 'Giriş Yap - OTO'
	/* eslint-enable camelcase */
};

const Login = () => {
	const [show2FA, setShow2FA] = useState(false);
	const { lang } = useParams();
	const { LangCountryCode } = useLocale();
	const changeLanguage = useChangeLanguage();
	const { state } = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [loginCredentials, setLoginCredentials] = useState({});
	const { showError } = useAlert();
	useFedCM();

	useEffect(() => {
		const language = languages.find((language) => language.systemCode === lang);
		if (language) {
			if (language !== LangCountryCode) changeLanguage(language.code);
		} else {
			navigate(
				{ pathname: `/login/${LangCountryCode}`, search: searchParams.toString() },
				{
					state: { redirectTo: state?.redirectTo }
				}
			);
		}
	}, [lang]);

	useEffect(() => {
		document.title = titles[LangCountryCode];
	}, [LangCountryCode]);

	useEffect(() => {
		const error = searchParams.get('error');
		if (error) {
			showError({
				message: error
			});
		}
	}, [searchParams]);

	return show2FA ? (
		<TwoFactorForm setShow2FA={setShow2FA} loginCredentials={loginCredentials} />
	) : (
		<LoginForm setShow2FA={setShow2FA} setLoginCredentials={setLoginCredentials} />
	);
};

const LoginForm = ({ setShow2FA, setLoginCredentials }) => {
	const schema = useMemo(
		() =>
			yup.object().shape({
				// eslint-disable-next-line camelcase
				e_mail: yup.string().nullable().required(),
				password: yup.string().nullable().required()
			}),
		[]
	);

	const refreshAccess = useRefreshToken();
	const { LangCountryCode } = useLocale();
	const { state } = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { showError } = useAlert();

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});

	const {
		handleSubmit,
		setFocus,
		watch,
		setError,
		formState: { isSubmitting }
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await axiosAuth.post(AUTH.LOGIN, {
				email: data.e_mail,
				password: data.password
			});

			if (response.status === 200 && response.data?.['2FARequired']) {
				setLoginCredentials(data);
				setShow2FA(true);
				return;
			}

			try {
				await isSupported().then(async (isSupported) => {
					isSupported && (await deleteFCMToken());
				});
			} catch {
				// do nothing
			}

			const refreshToken = response?.data?.refreshToken;
			if (refreshToken) localStorage.setItem('REFRESH_TOKEN', refreshToken);
			await refreshAccess(refreshToken);

			window.dataLayer.push({
				event: 'login',
				email: data.e_mail || ''
			});

			if (state?.redirectTo) {
				navigate(state.redirectTo.pathname + state.redirectTo.search);
			} else {
				navigate(PATHS.BASE);
			}
		} catch (error) {
			if (error.response?.data?.msg === 'too many attempts please wait 5 minutes') {
				showError({
					message: 'errorMsg.tooManyAttemptsTryAgain5MinutesLater'
				});
			} else if (error.response?.data?.msg === 'too many attempts please wait 30 minutes') {
				showError({
					message: 'errorMsg.tooManyAttemptsTryAgain30MinutesLater'
				});
			} else {
				setError(
					'e_mail',
					{ type: 'custom', message: 'pleaseCheckYourUsernameAndPassword' },
					{ shouldFocus: true }
				);
			}

			AppLogger.error(error);
		}
	};

	const handleKeyPress = ({ charCode }) => {
		// Enter key
		if (charCode === 13) {
			if (watch('e_mail') && !watch('password')) {
				setFocus('password');
			} else if (watch('password') && !watch('e_mail')) {
				setFocus('e_mail');
			} else {
				handleSubmit(onSubmit, (errors) => {
					AppLogger.log(errors);
				})();
			}
		}
	};

	return (
		<Form
			formMethods={formMethods}
			borderless
			style={{
				display: 'flex',
				justifyContent: 'center'
			}}>
			<S.Container>
				<S.Content>
					<div>
						<FormItem>
							<Typography noWrap variant="h3" fontWeight="700" gutterBottom>
								welcomeBackOTO
							</Typography>
						</FormItem>
						{import.meta.env.VITE_PLATFORM !== 'desktop' && (
							<>
								<FormItem>
									<SigninWithGoogle disabled={isSubmitting} />
								</FormItem>
								<FormItem>
									<S.Seperator>
										<Typography variant="p2">key.or</Typography>
									</S.Seperator>
								</FormItem>
							</>
						)}

						<FormItem label="emailLogin" required>
							<StringInput name="e_mail" onKeyPress={handleKeyPress} placeholder="emailPlaceholder" />
						</FormItem>
						<FormItem label="password" required>
							<PasswordInput name="password" onKeyPress={handleKeyPress} />
						</FormItem>

						<S.ButtonLink
							variant="transparent"
							label="forgotPassword"
							className="mt-3"
							disabled={isSubmitting}
							onClick={() => {
								navigate(
									{
										pathname: PATHS.FORGOT_PASSWORD,
										search: searchParams.toString()
									},
									{ state: { from: 'login' } }
								);
							}}
						/>
					</div>
					<FormItem style={{ paddingTop: '2rem' }}>
						<Button
							size="large"
							loading={isSubmitting}
							style={{ width: '100%' }}
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}>
							<Typography className="button-text" fontWeight="500">
								loginToMyAccount
							</Typography>
						</Button>
					</FormItem>
				</S.Content>
			</S.Container>
			<FormItem>
				<S.ButtonContainer>
					<Typography>newAtOTO</Typography>
					<S.ButtonLink
						variant="transparent"
						label="createAFreeAccount"
						disabled={isSubmitting}
						onClick={() => {
							navigate(
								{
									pathname: `/register/${LangCountryCode}`,
									search: searchParams.toString()
								},
								{
									state: { redirectTo: state?.redirectTo }
								}
							);
						}}
					/>
				</S.ButtonContainer>
			</FormItem>
		</Form>
	);
};

const TwoFactorForm = ({ setShow2FA, loginCredentials }) => {
	const [value, setValue] = useState([null, null, null, null, null, null]);
	const [rememberDevice, setRememberDevice] = useState(false);
	const [useBackupCode, setUseBackupCode] = useState(false);
	const refreshAccess = useRefreshToken();
	const { state } = useLocation();
	const navigate = useNavigate();
	const { showError } = useAlert();
	const [loading, setLoading] = useState(false);

	const submit = async () => {
		setLoading(true);
		const code = value.join('');

		try {
			const response = await axiosAuth.post(AUTH.LOGIN_WITH_TOTP, {
				email: loginCredentials.e_mail,
				password: loginCredentials.password,
				activationCode: code,
				rememberDevice,
				isBackupCode: useBackupCode
			});

			const { status } = response;

			if (status !== 200) {
				showError({
					message: 'errorMsg.incorrectOTP'
				});
				AppLogger.error(response);

				return;
			}

			try {
				await isSupported().then(async (isSupported) => {
					isSupported && (await deleteFCMToken());
				});
			} catch {
				// do nothing
			}

			const refreshToken = response?.data?.refreshToken;
			if (refreshToken) localStorage.setItem('REFRESH_TOKEN', refreshToken);
			await refreshAccess(refreshToken);

			if (state?.redirectTo) {
				navigate(state.redirectTo.pathname + state.redirectTo.search);
			} else {
				navigate(PATHS.BASE);
			}
		} catch (error) {
			if (error.response?.data?.msg === 'too many attempts please wait 5 minutes') {
				showError({
					message: 'errorMsg.tooManyAttemptsTryAgain5MinutesLater'
				});
			} else if (error.response?.data?.msg === 'too many attempts please wait 30 minutes') {
				showError({
					message: 'errorMsg.tooManyAttemptsTryAgain30MinutesLater'
				});
			} else {
				showError({
					message: 'errorMsg.incorrectOTP'
				});
			}

			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<S.Container>
				<S.MFAContent>
					<S.MFATitleContainer>
						{useBackupCode && (
							<Button
								onClick={() => {
									setValue([null, null, null, null, null, null]);
									setUseBackupCode(false);
								}}
								icon={faArrowLeft}
								iconButton
								variant="borderless"
								iconSize="large"
							/>
						)}
						<Typography variant="h4" noWrap>
							{useBackupCode ? 'login.2FABackupCodesTitle' : 'login.2FATitle'}
						</Typography>
					</S.MFATitleContainer>
					<S.OTPContainer>
						<Typography fontWeight="500" noWrap>
							{useBackupCode ? 'login.2FABackupCodesDescription' : 'login.2FADescription'}
						</Typography>
						<OTPInput
							value={value}
							setValue={setValue}
							length={6}
							loading={loading}
							onFilledOnce={submit}
						/>
					</S.OTPContainer>
					{!useBackupCode && import.meta.env.VITE_PLATFORM !== PLATFORM.desktop && (
						<FormItem label="login.rememberDevice" direction="row-reverse">
							<SwitchInput
								value={rememberDevice}
								setValue={() => {
									setRememberDevice(!rememberDevice);
								}}
							/>
						</FormItem>
					)}
					<S.ConfirmButton
						disabled={value?.includes(null)}
						label="confirm"
						fullWidth
						onClick={submit}
						size="x-large"
						loading={loading}
					/>
					{!useBackupCode && (
						<S.MFABottomButtonContainer>
							<Typography>login.cantAccessMFA</Typography>
							<S.ButtonLink
								variant="transparent"
								label="login.useABackupCode"
								onClick={() => {
									setValue([null, null, null, null, null, null]);
									setUseBackupCode(true);
								}}
							/>
						</S.MFABottomButtonContainer>
					)}
				</S.MFAContent>
			</S.Container>
			<FormItem>
				<S.ButtonContainer>
					<S.ButtonLink
						variant="transparent"
						label="logout"
						onClick={() => {
							setShow2FA(false);
						}}
					/>
				</S.ButtonContainer>
			</FormItem>
		</>
	);
};

export default Login;
